import React, { Component } from 'react';
import { Button, Alert, Table, Badge, Form, Modal, Row, Col } from 'react-bootstrap';
import { GoTrash, GoSignIn } from "react-icons/go";
import Select from 'components/controls/select';
import ConfirmModal from 'components/controls/confirmModal';
import UserIdentity from 'components/controls/userIdentity';
import AccountTypes from 'context/accountTypes';

import Clipboard from "blob_types/blobhub/compute/workflow/controls/common/clipboard";

class AddAccountModal extends Component {
  constructor(props) {
    super(props);

    // Options come from the registry, never from a literal here: a future kind of account is a new
    // registry entry, and it shows up in this picker automatically.
    this.typeOptions = AccountTypes.creatableTypes().map(entry => ({ value: entry.type, label: entry.label }));

    this.state = { type: this.typeOptions[0], name: "" };
    this.form = React.createRef();
  }

  onEnter = () => {
    this.setState({ type: this.typeOptions[0], name: "" });
  }

  handleAdd = () => {
    if ( !this.form.current.reportValidity() ) {
      return;
    }

    this.props.handleAddAccount({
      type: this.state.type.value,
      name: this.state.name
    });

    this.props.onHide();
  }

  render() {
    return (
      <Modal
        onEnter={this.onEnter}
        onHide={this.props.onHide}
        show={this.props.show}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Add New Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form ref={this.form}>
            <Form.Group>
              <Form.Label>
                <strong>Type</strong> <span className="text-muted">(required)</span>
              </Form.Label>
              <Row>
                <Col sm={4}>
                  <Select
                    options={this.typeOptions}
                    value={this.state.type}
                    onChange={(option) => this.setState({ type: option })}
                  />
                </Col>
              </Row>
              <Form.Text muted>
                {AccountTypes.of({ type: this.state.type.value }).description}
              </Form.Text>
            </Form.Group>
            <hr/>

            <Form.Group>
              <Form.Label>
                <strong>Name</strong> <span className="text-muted">(required)</span>
              </Form.Label>
              <Row>
                <Col sm={6}>
                  <Form.Control
                    required
                    type="text"
                    size="sm"
                    autoFocus
                    maxLength={128}
                    value={this.state.name}
                    onChange={(event) => this.setState({ name: event.target.value })}
                  />
                </Col>
              </Row>
              <Form.Text muted>
                Names are free-form and do not have to be unique. An account has no alias: it is
                identified by this name and by its id.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.props.onHide}>Close</Button>
          <Button variant="success" onClick={this.handleAdd}>Add</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

class Account extends Component {
  constructor(props) {
    super(props);

    this.state = { confirmModalShow: false };
  }

  render() {
    const account = this.props.account;
    return (
      <>
        <tr>
          <td>
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-baseline">
                <UserIdentity user={account} fallback={account.id} link={false}/>
                &nbsp;&nbsp;
                <Clipboard text={account.id} />
                &nbsp;&nbsp;
                <Badge variant="secondary" className="title-tag-badge">{AccountTypes.of(account).label}</Badge>
              </div>

              <div className="ml-auto">
                {!this.props.accountsInterface.modifying &&
                  <>
                    {/* Stock Bootstrap utilities rather than a stylesheet rule: an inline <svg> sits
                        on the text baseline, so a flex row that centres its items is what actually
                        aligns an icon against its label. */}
                    <Button
                      variant="light"
                      size="sm"
                      className="d-inline-flex align-items-center"
                      title={`Act as ${account.name}`}
                      aria-label={`Act as ${account.name}`}
                      onClick={() => this.props.accountsInterface.actAs(account)}
                    ><GoSignIn className="mr-1"/> Act as</Button>
                    &nbsp;&nbsp;
                    <Button
                      variant="danger"
                      className="btn-icon"
                      title={`Retire ${account.name}`}
                      aria-label={`Retire ${account.name}`}
                      onClick={() => this.setState({ confirmModalShow: true })}
                    ><GoTrash /></Button>
                  </>
                }
              </div>
            </div>
          </td>
        </tr>

        <ConfirmModal
          show={this.state.confirmModalShow}
          onHide={() => this.setState({ confirmModalShow: false })}
          onConfirm={() => this.props.accountsInterface.retire(account)}
          title="Retire Account"
          confirmLabel="Retire"
          body={
            <>
              Are you sure you want to retire <strong>{account.name}</strong>? Its API keys are
              deleted and it is removed from every organization it belongs to, so any integration
              using it stops working immediately. The record itself is kept so that everything it
              authored still resolves. This is terminal and cannot be undone.
            </>
          }
        />
      </>
    );
  }
}

/**
 * Accounts — create, list and retire the service accounts a user or an organization owns, and open
 * the console as one of them.
 *
 * Mounted from both Profile settings (target=user) and Org settings (target=org) — the API scopes
 * both the same way, so one component serves both. Deliberately NOT a place to manage an account's
 * API keys: an acting token is scoped to the single account it names, so keys are managed from
 * inside each account, by acting as it and using the ordinary API Keys section.
 */
export default class Accounts extends Component {
  constructor(props) {
    super(props);

    this.state = {
      accounts: null,
      modifying: false,
      // Names the one operation in flight and carries the account it concerns, e.g.
      // { kind: "acting", name }. `modifying` keeps doing exactly what it always did (hiding
      // controls); this is only for choosing what the in-flight Alert says.
      progress: null,
      error: null,
      addAccountModalShow: false,
      actAs: this.actAs,
      retire: this.retire
    };
  }

  componentDidMount() {
    this.loadAccounts();
  }

  loadAccounts = () => {
    this.props.app.api.usersTargetTargetIdGet(this.props.target, this.props.targetId)
      .then(response => {
        this.setState({ accounts: response.data.users, error: null });
      }, error => {
        this.setState({ accounts: [], error: error.message });
      });
  }

  handleAddAccount = (account) => {
    this.setState({ modifying: true, error: null, progress: { kind: "creating" } });

    this.props.app.api.usersTargetTargetIdPost(this.props.target, this.props.targetId, account)
      .then(_ => {
        this.setState({ modifying: false, progress: null });
        this.loadAccounts();
      }, error => {
        this.setState({ modifying: false, progress: null, error: error.message });
      });
  }

  retire = (account) => {
    this.setState({ modifying: true, error: null, progress: { kind: "retiring", name: account.name } });

    this.props.app.api.usersIdDelete(account.id)
      .then(_ => {
        this.setState({ modifying: false, progress: null });
        this.loadAccounts();
      }, error => {
        this.setState({ modifying: false, progress: null, error: error.message });
      });
  }

  actAs = (account) => {
    this.setState({ modifying: true, error: null, progress: { kind: "acting", name: account.name } });

    // startActing navigates on success, so there is no success branch to render. That also means
    // the "acting" progress alert is deliberately never cleared on success: it stays up — the only
    // thing on screen — until the browser leaves the page. Do not "fix" this by clearing it here.
    this.props.app.api.startActing(account)
      .then(undefined, error => {
        this.setState({ modifying: false, progress: null, error: error.message });
      });
  }

  // What the in-flight Alert says, keyed off `this.state.progress.kind`. Returns null when there is
  // no progress to show (render() only calls this while `this.state.progress` is truthy, but this
  // stays defensive so a future caller doesn't have to re-derive that invariant).
  progressMessage = () => {
    if ( !this.state.progress ) {
      return null;
    }
    switch ( this.state.progress.kind ) {
      case "creating": return "Creating account...";
      case "retiring": return `Retiring ${this.state.progress.name}...`;
      case "acting": return `Opening the console as ${this.state.progress.name}...`;
      default: return null;
    }
  }

  render() {
    const sectionLabel = AccountTypes.sectionLabel();

    // The create control carries this section's domain icon, the way the sibling sections carry a key
    // (API Keys) and a person (Members). It is the glyph of the kind of account the button creates,
    // taken from the registry so a new type brings its own icon with it. With more than one creatable
    // type there is no single kind to depict, so it falls back to the neutral glyph explicitly rather
    // than letting `of()` resolve a null type to `human` by accident.
    const creatable = AccountTypes.creatableTypes();
    const CreateGlyph = 1 === creatable.length ? creatable[0].glyph : AccountTypes.of({}).glyph;

    return (
      <>
        <h5>{sectionLabel}</h5>
        <hr/>

        <div>
          An identity for a machine — a worker, a CI runner, an agent — that holds its own API keys and
          authors content under its own name. Grant it access through Members, the same as a person.
        </div>

        {this.state.accounts && 0 !== this.state.accounts.length &&
          <div className="content-row">
            <Table hover bordered style={{"marginBottom": "0px"}}>
              <tbody>
                {this.state.accounts.map((account) => {
                  return (
                    <Account
                      key={account.id}
                      app={this.props.app}
                      accountsInterface={this.state}
                      account={account}
                    />
                  );
                })}
              </tbody>
            </Table>
          </div>
        }

        {!this.state.modifying &&
          <div className="content-row">
            <Button
              variant="success"
              size="sm"
              className="d-inline-flex align-items-center"
              onClick={() => this.setState({ addAccountModalShow: true, error: null })}
            ><CreateGlyph className="mr-1"/> New</Button>

            <span className="text-muted text-note">
              &nbsp;&nbsp;&nbsp;Create an account, then grant it access and keys
            </span>
          </div>
        }

        {this.state.progress &&
          <div className="content-row">
            <Alert variant="primary">{this.progressMessage()}</Alert>
          </div>
        }

        {this.state.error &&
          <div className="content-row">
            <Alert variant="warning">{this.state.error}</Alert>
          </div>
        }

        <AddAccountModal
          show={this.state.addAccountModalShow}
          onHide={() => this.setState({ addAccountModalShow: false })}
          handleAddAccount={this.handleAddAccount}
        />
      </>
    );
  }
}
